import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { APIKey } from '@feathr/blackbox';
import { ConfirmModal, ContextMenu, CopyToClipboardButton, toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { getIconForAction, useToggle } from '@feathr/hooks';

interface IProps {
  apiKey: APIKey;
}

/** Row Options menu: Copy (legacy keys only) + Delete (revoke, irreversible). */
function APIKeyOptionsV2({ apiKey }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { APIKeys } = useStore();
  const [isDeleteModalOpen, toggleDeleteModalOpen] = useToggle(false);

  // Legacy (Zapier) keys carry a recoverable JWT; service keys are opaque.
  const recoverableKey = apiKey.get('kind') === 'legacy' ? apiKey.get('key') : undefined;

  async function handleDelete(): Promise<void> {
    // Collection.delete resolves even on failure — it flags the model, not throws.
    await apiKey.delete();
    if (apiKey.isErrored) {
      toast(t("Couldn't delete the API key. Try again."), { type: 'error' });
      return;
    }
    APIKeys.refreshApiCache();
    toast(t('Deleted "{{name}}".', { name: apiKey.name }), { type: 'success' });
    toggleDeleteModalOpen();
  }

  return (
    <>
      <ContextMenu buttonType={'icon'} name={'api-key-options'} position={'left-start'}>
        {!!recoverableKey && (
          <CopyToClipboardButton
            component={'ContextMenuItem'}
            name={'copy-key'}
            successMessage={t('Copied the API key to your clipboard.')}
            t={t}
            text={recoverableKey}
          >
            {t('Copy API key')}
          </CopyToClipboardButton>
        )}
        <ContextMenu.Item
          name={'delete-key'}
          onClick={toggleDeleteModalOpen}
          prefix={getIconForAction('delete')}
          theme={'danger'}
        >
          {t('Delete')}
        </ContextMenu.Item>
      </ContextMenu>
      {isDeleteModalOpen && (
        <ConfirmModal
          alertTitle={t("Are you sure you want to delete {{name}}? This action can't be undone.", {
            name: apiKey.name,
          })}
          cancelButtonText={t('Cancel')}
          confirmationPhrase={apiKey.name}
          confirmButtonText={t('Delete')}
          confirmButtonType={'danger'}
          onClose={toggleDeleteModalOpen}
          onConfirm={handleDelete}
          opened={isDeleteModalOpen}
          t={t}
          title={t('Delete API key?')}
        />
      )}
    </>
  );
}

export default observer(APIKeyOptionsV2);
